import { api } from "@/lib/api";

const VISITOR_KEY = "ark_visitor_id";
const SESSION_KEY = "ark_session_id";
const SESSION_START_KEY = "ark_session_start";
const SESSION_END_SENT_KEY = "ark_session_end_sent";
const PATHS_KEY = "ark_tracked";

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function isAdmin() {
  return !!localStorage.getItem("ark_admin_token");
}

function getVisitorId() {
  if (isAdmin()) return null;
  let v = localStorage.getItem(VISITOR_KEY);
  if (!v) {
    v = uuid();
    localStorage.setItem(VISITOR_KEY, v);
  }
  return v;
}

function getSessionId() {
  let s = sessionStorage.getItem(SESSION_KEY);
  if (!s) {
    s = uuid();
    sessionStorage.setItem(SESSION_KEY, s);
    sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    sessionStorage.removeItem(SESSION_END_SENT_KEY);
  }
  return s;
}

function send(payload) {
  api.post("/track", payload).catch(() => {});
}

// Track once per tab+path (used for plain page-views)
export function trackOnce(path = "/") {
  const visitor = getVisitorId();
  if (!visitor) return;
  const session = getSessionId();
  const cacheKey = `${PATHS_KEY}:${path}`;
  if (sessionStorage.getItem(cacheKey)) return;
  sessionStorage.setItem(cacheKey, "1");

  let event = null;
  let piece_id = null;
  if (path === "/") event = "view_home";
  else if (path.startsWith("/piece/")) {
    event = "view_piece";
    piece_id = path.replace("/piece/", "");
  }
  send({ visitor_id: visitor, session_id: session, path, event, piece_id });
}

// Track an arbitrary engagement event (wizard step, quote submit, …)
export function trackEvent(event, extra = {}) {
  const visitor = getVisitorId();
  if (!visitor) return;
  const session = getSessionId();
  send({ visitor_id: visitor, session_id: session, event, ...extra });
}

// Send a single session_end with total time-on-site when the tab is being closed
function sendSessionEnd() {
  if (sessionStorage.getItem(SESSION_END_SENT_KEY)) return;
  const visitor = localStorage.getItem(VISITOR_KEY);
  const session = sessionStorage.getItem(SESSION_KEY);
  const start = sessionStorage.getItem(SESSION_START_KEY);
  if (!visitor || !session || !start || isAdmin()) return;
  const dur = Date.now() - parseInt(start, 10);
  if (dur < 2000) return; // ignore bounces shorter than 2 seconds
  sessionStorage.setItem(SESSION_END_SENT_KEY, "1");

  const url = `${api.defaults.baseURL || ""}/track`;
  const payload = JSON.stringify({
    visitor_id: visitor,
    session_id: session,
    event: "session_end",
    duration_ms: dur,
  });
  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    } catch (err) {
      // Beacon failed (very rare) — fall through to fetch
      if (process.env.NODE_ENV !== "production") {
        console.warn("sendBeacon failed", err);
      }
    }
  }
  // Fallback: best-effort fetch with keepalive
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    });
  } catch (err) {
    // Tab is closing — nothing to do
    if (process.env.NODE_ENV !== "production") {
      console.warn("session_end fetch failed", err);
    }
  }
}

// Listen once for tab/page hide
if (typeof window !== "undefined" && !window.__arkSessionEndBound) {
  window.__arkSessionEndBound = true;
  window.addEventListener("pagehide", sendSessionEnd);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") sendSessionEnd();
  });
}
